import moment from "moment";

import ChatChannelSerializer from "issara-sdk/types/ChatChannelSerializer_apps_MSG";
import PatientAppointmentSerializer from "issara-sdk/types/PatientAppointmentSerializer_apps_QUE";
import PatientQueueSerializer from "issara-sdk/types/PatientQueueSerializer_apps_PRX";
import PatientSerializer from "issara-sdk/types/PatientSerializer_apps_REG";
import ProxyPatientSerializer from "issara-sdk/types/ProxyPatientSerializer_apps_PRX";

import { formatDate } from "react-lib/utils/dateUtils";

import { dash } from "../common/displayTextV4";
import { stripHtml } from "../patient/resultModelV4";

// ---------- ข้อมูลจาก API ----------

/** แถวของ chatRoomList: PatientQueueList + field ที่ GetChatChannelList เติม */
export type ChatRoom = Partial<PatientQueueSerializer> &
  Partial<
    Pick<ChatChannelSerializer, "chat_channel_name_autogen" | "latest_message_content" | "division">
  >;

/** selectedPatientChat (REG/patient/{pk}/) */
export type ChatPatient = Partial<PatientSerializer>;

/** selectedProxyPatient (PRX/proxy-patient/{pk}/) */
export type ChatProxyPatient = Partial<ProxyPatientSerializer>;

/** selectedPatientAppointmentList (QUE/patient-appointment/) */
export type ChatAppointment = Partial<PatientAppointmentSerializer>;

// ---------- แท็บ ----------

export type ChatTabKey = "profile" | "form" | "booking" | "note";

export const CHAT_TABS: { key: ChatTabKey; label: string }[] = [
  { key: "profile", label: "Profile" },
  { key: "form", label: "Form" },
  { key: "booking", label: "Booking" },
  { key: "note", label: "Note" },
];

export const PENDING_TEXT: Record<"form" | "note", string> = {
  form: "ฟอร์มที่ผู้รับบริการกรอก · รอ Requirement",
  note: "บันทึกภายใน (Note) · รอ Requirement",
};

/** แท็บที่แสดงการ์ดข้อมูลส่วนบุคคล + นัดหมาย (Profile กับ Booking แสดงเหมือนกัน) */
export const tabShowsCards = (tab: ChatTabKey): boolean => tab === "profile" || tab === "booking";

// ---------- รายการห้อง ----------

const MEDIA_TEXT: Record<string, string> = {
  file: "ส่งไฟล์",
  image: "ส่งรูปภาพ",
  video: "ส่งวิดีโอ",
};

export const roomName = (room?: ChatRoom | null): string =>
  (room?.chat_channel_name_autogen ?? "").trim();

/** อักษรแรกของชื่อ (avatar) */
export const chatInitial = (name?: string | null): string => {
  const text = (name ?? "").trim();
  return text ? Array.from(text)[0] : "";
};

/** วันที่ของแถวห้อง: วันนี้ = HH:mm, วันอื่น = DD/MM/YYYY (พ.ศ.) */
export const chatListDate = (value?: string | null, now = moment()): string => {
  if (!value) return "";
  const date = moment(value);
  if (!date.isValid()) return "";
  return date.isSame(now, "day") ? date.format("HH:mm") : formatDate(date);
};

/** ข้อความล่าสุดของห้อง 1 บรรทัด */
export const latestMessageText = (room: ChatRoom, userId: string): string => {
  const media = MEDIA_TEXT[room.latest_message_content_type ?? ""];
  if (media) {
    const author =
      room.latest_message_author === Number(userId)
        ? "คุณ"
        : `${room.latest_message_author_name ?? ""} `;
    return `${author}${media}`;
  }
  return stripHtml(room.latest_message_content ?? "");
};

export const unreadBadge = (count?: number | null): string => {
  const value = Number(count) || 0;
  if (value <= 0) return "";
  return value > 99 ? "99+" : String(value);
};

/** กรองห้องตามชื่อ (substring) */
export const filterChatRooms = (rooms: ChatRoom[], query: string): ChatRoom[] => {
  const text = query.trim().toLowerCase();
  if (!text) return rooms;
  return rooms.filter((room) => roomName(room).toLowerCase().includes(text));
};

// ---------- ข้อมูลส่วนบุคคล ----------

export type InfoRow = { label: string; value: string };

export const genderText = (gender?: string | null): string => {
  if (gender === "M" || gender === "1") return "ชาย";
  if (gender === "F" || gender === "2") return "หญิง";
  return "";
};

/** แถวของการ์ด "ข้อมูลส่วนบุคคล" */
export const patientInfoRows = (
  patient?: ChatPatient | null,
  proxy?: ChatProxyPatient | null,
): InfoRow[] => {
  const name = (patient?.full_name ?? "").trim();
  const hn = (patient?.hn ?? "").trim();
  return [
    { label: "ชื่อ - นามสกุล", value: name ? `${name}${hn ? ` (HN ${hn})` : ""}` : "-" },
    { label: "เพศ", value: genderText(proxy?.extra_data?.gender) || "-" },
    { label: "วันเกิด", value: dash(patient?.birthdate) },
    { label: "อายุ", value: dash(patient?.full_age) },
    { label: "เบอร์โทร", value: dash(patient?.present_address?.tel_mobile) },
    { label: "E-mail", value: dash(patient?.present_address?.email) },
  ];
};

/** ชื่อใต้ avatar ใหญ่ของแผงขวา */
export const patientDisplayName = (patient?: ChatPatient | null, room?: ChatRoom | null): string =>
  [patient?.first_name, patient?.last_name].filter(Boolean).join(" ").trim() || roomName(room);

// ---------- นัดหมาย ----------

type StatusTone = "amber" | "ok" | "sky" | "danger";

export type AppointmentView = {
  id: number;
  provider: string;
  statusText: string;
  statusTone: StatusTone;
  date: string;
  time: string;
};

/** key ของ loadingStatus รายการนัด (PatientAppointmentListCard ใน MSG/StaffChatInterface) */
export const APPOINTMENT_LIST_CARD = "GET_PATIENT_APPOINTMENT_LIST";

export const APPOINTMENT_STATUS = {
  request: 1,
  confirm: 2,
  revise: 3,
  cancel: 5,
} as const;

const STATUS_VIEW: Record<number, { text: string; tone: StatusTone }> = {
  [APPOINTMENT_STATUS.request]: { text: "รอยืนยัน", tone: "amber" },
  [APPOINTMENT_STATUS.confirm]: { text: "ยืนยันแล้ว", tone: "ok" },
  [APPOINTMENT_STATUS.revise]: { text: "ถูกเลื่อนนัด", tone: "sky" },
  [APPOINTMENT_STATUS.cancel]: { text: "ยกเลิก", tone: "danger" },
};

/** "dd/mm/yyyy [HH:mm]" → { date, time } */
export const splitEstimatedAt = (value?: string | null): { date: string; time: string } => {
  const text = (value ?? "").trim();
  const match = text.match(/^(\d{1,2}\/\d{1,2}\/\d{4})\s*(?:\[(\d{1,2}:\d{2})\])?/);
  if (!match) return { date: text, time: "" };
  return { date: match[1], time: match[2] ?? "" };
};

const sortStamp = (item: ChatAppointment): string => {
  const match = (item.estimated_at ?? "").match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s*\[(\d{1,2}:\d{2})\])?/,
  );
  if (!match) return "";
  const [, day, month, year, time] = match;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")} ${(time ?? "00:00").padStart(5, "0")}`;
};

/** รายการนัดที่แสดงในการ์ด: สถานะ รอยืนยัน/ยืนยันแล้ว/ถูกเลื่อนนัด/ยกเลิก เรียงเก่า → ใหม่ */
export const appointmentViews = (list?: ChatAppointment[] | null): AppointmentView[] => {
  const rows = (list ?? []).filter((item) => !!STATUS_VIEW[Number(item.status)]);
  rows.sort((a, b) => {
    const stampA = sortStamp(a);
    const stampB = sortStamp(b);
    if (stampA !== stampB) {
      if (!stampA) return 1;
      if (!stampB) return -1;
      return stampA < stampB ? -1 : 1;
    }
    return Number(a.id ?? 0) - Number(b.id ?? 0);
  });
  return rows.map((item) => {
    const status = STATUS_VIEW[Number(item.status)];
    const { date, time } = splitEstimatedAt(item.estimated_at);
    return {
      id: Number(item.id ?? 0),
      provider: dash(item.division_name),
      statusText: status.text,
      statusTone: status.tone,
      date,
      time,
    };
  });
};

// ---------- เลือกห้อง ----------

/** event ที่ต้องยิงเมื่อเลือกห้องที่มี patient (ชุดเดียวกับ V3 ProfilePanel ที่หน้านี้ใช้) */
export const patientDetailEvents = (
  room: ChatRoom,
): { message: string; params: Record<string, unknown> }[] => {
  const patient = room.patient;
  if (!patient) return [];
  return [
    { message: "GetPatientChatDetail", params: { patient } },
    { message: "GetPatientAppointmentList", params: { patient, division: room.division } },
    { message: "GetProxyPatient", params: { patient } },
    {
      message: "GetOfficialAccountList",
      params: { search: "", card: "GET_LIST_OFFICIAL_ACCOUNT" },
    },
  ];
};
